/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  combineReducers,
  composeReducers,
  composeSetters,
  newDirtyRowWithVersion,
  onChangeReducer,
  removeDirtyRow,
  setDirty,
  setEntityField,
  setNewDirtyType,
  setUpdatedDirtyType,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { find } from 'lodash';
import {
  getHpeCostCenterId,
  getHpeItemId,
  HpeCostCenter,
  HpeDetailsGetCostCenter,
  mergeLoadedCostCenter,
} from '../../../resources/hpe-details.model';
import {
  hpeCcTable,
  HpeDetailsState,
  HpeDetailsStateEvent,
  HpeDetailsStateEventType,
  hpeItemTable,
  queryHpeCostCenterItemCount,
  queryHpeCostCenterItems,
} from '../hpe-details.state';
import { selectionConsistencyReducer } from './hpe-details-selection.reducers';

const setCostCenterField = composeSetters(setEntityField, setUpdatedDirtyType);

export const updateCostCenterItemCount = (state: HpeDetailsState, cc: HpeCostCenter) =>
  setEntityField(cc, 'count', queryHpeCostCenterItemCount(state, getHpeCostCenterId(cc)));

export const hpeDetailsCcReducer = composeReducers(
  loadCostCenterReducer,
  onChangeReducer(
    combineReducers({
      hpCostCenters: costCentersReducer,
    }),
    rootCostCenterOnChangeReducer,
    toOnChangeReducer(selectionConsistencyReducer),
  ),
);

function loadCostCenterReducer(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.CostCenterLoad:
      return mergeCostCenter(state, event.costCenterId, event.costCenter);
    default:
      return state;
  }
}

function costCentersReducer(costCenters: Table<HpeCostCenter>,
                            event: HpeDetailsStateEvent): Table<HpeCostCenter> {
  switch (event.type) {
    case HpeDetailsStateEventType.CostCenterAdd:
      return hpeCcTable.merge(costCenters, event.costCenter, newDirtyRowWithVersion);
    case HpeDetailsStateEventType.CostCenterSetField:
      return hpeCcTable.mergeById(
        costCenters,
        event.costCenterId,
        (row: HpeCostCenter) => setCostCenterField(row, event.name, event.value));
    case HpeDetailsStateEventType.CostCenterRemove:
      return hpeCcTable.mergeById(costCenters, event.costCenterId, removeDirtyRow);
    default:
      return costCenters;
  }
}

function rootCostCenterOnChangeReducer(_: HpeDetailsState,
                                       state: HpeDetailsState,
                                       event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.CostCenterAdd:
    case HpeDetailsStateEventType.CostCenterSetField:
    case HpeDetailsStateEventType.CostCenterRemove:
      return setDirty(state);
    default:
      return state;
  }
}

export function addOrUpdateCostCenters(state: HpeDetailsState, costCenters: HpeCostCenter[]): HpeDetailsState {
  const ccNewOrUpdatedIds = [state.hpComponentsCcId].concat(costCenters.map(getHpeCostCenterId));
  return setEntityField(
    state,
    'hpCostCenters',
    hpeCcTable.mergeAllById(
      state.hpCostCenters,
      ccNewOrUpdatedIds,
      (row, id) => row ? setUpdatedDirtyType(row) : setNewDirtyType(find(costCenters, { costCenterId: id })!)));
}

export function markCostCentersAsDirty(state: HpeDetailsState, ids: ID[]): HpeDetailsState {
  // sometimes referenced cost centers can absent (due to bugs in legacy application)
  return updateCostCenters(state, ids, (row) => row ? setUpdatedDirtyType(row) : void 0);
}

export function updateCostCenters(state: HpeDetailsState,
                                     ids: ID[],
                                     transform: (data: Maybe<HpeCostCenter>,
                                                 id: ID) => Maybe<HpeCostCenter>): HpeDetailsState {
  return setEntityField(
    state,
    'hpCostCenters',
    hpeCcTable.mergeAllById(state.hpCostCenters, ids, transform));
}

export function mergeCostCenter(state: HpeDetailsState,
                                costCenterId: ID,
                                costCenter: HpeDetailsGetCostCenter): HpeDetailsState {
  return {
    ...state,
    hpCostCenters: hpeCcTable.mergeById(
      state.hpCostCenters,
      costCenterId,
      (current) => mergeLoadedCostCenter(current!, costCenter)),
    hpItems: hpeItemTable.replaceAll(
      state.hpItems,
      queryHpeCostCenterItems(state, costCenterId).map(getHpeItemId),
      costCenter.items,
      (oldItem, newItem) => {
        if (oldItem) {
          return { ...oldItem, ...newItem };
        } else {
          return newItem;
        }
      }),
  };
}
